import { useMemo, useState } from 'react';
import {
  Bar,
  CartesianGrid,
  Cell,
  ComposedChart,
  Line,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { useI18n } from '@/hooks/useI18n';
import { useChartTooltipTrigger } from '@/hooks/useChartTooltipTrigger';
import { Skeleton } from '@/components/ui/skeleton';
import { formatNumber } from '@/lib/format';
import {
  formatAnalyticsBucket,
  formatAnalyticsValue,
  prepareAnalyticsSeries,
} from '@/lib/analyticsSeries';
import type {
  AnalyticsChartPoint,
  AnalyticsSeriesMode,
  AnalyticsSeriesPoint,
} from '@/lib/analyticsSeries';
import type { Granularity } from '@/types/enums';
import { AnalyticsEmpty } from './AnalyticsEmpty';

interface LineSeriesChartProps {
  data: AnalyticsSeriesPoint[];
  color: string;
  granularity: Granularity;
  variant?: 'bar' | 'line';
  mode?: AnalyticsSeriesMode;
  valueLabel: string;
  loading?: boolean;
  ariaLabel?: string;
  height?: number;
  now?: number;
}

export function LineSeriesChart({
  data,
  color,
  granularity,
  variant = 'line',
  mode = 'bucket',
  valueLabel,
  loading,
  ariaLabel,
  height,
  now,
}: LineSeriesChartProps) {
  const { locale } = useI18n();
  const tooltipTrigger = useChartTooltipTrigger();
  const [chartWidth, setChartWidth] = useState(0);
  const chartData = useMemo(
    () => prepareAnalyticsSeries(data, granularity, mode, now),
    [data, granularity, mode, now],
  );
  if (loading)
    return <Skeleton className="h-[230px] w-full rounded-[14px] sm:h-[280px]" style={{ height }} />;
  if (!chartData.some((point) => point.v !== null)) return <AnalyticsEmpty />;
  const tickStride = Math.max(1, Math.ceil(chartData.length / 8));
  const ticks = chartData
    .filter((_, index) => index % tickStride === 0 || index === chartData.length - 1)
    .map((point) => point.x);
  // Keep consecutive bars close on short series. A null marks missing time;
  // retain the full time scale for those gaps and for line charts.
  const continuousBars = variant === 'bar' && chartData.every((point) => point.v !== null);
  const maxBarSize = continuousBars
    ? Math.min(72, Math.max(32, ((chartWidth - 60) / chartData.length) * 0.8))
    : 32;
  const xPadding = continuousBars
    ? Math.max(maxBarSize / 2, (chartWidth - 60 - Math.max(1, chartData.length - 1) * 96) / 2)
    : 16;

  return (
    <div
      className="h-[230px] w-full min-w-0 sm:h-[280px]"
      style={{ height }}
      aria-label={ariaLabel}
    >
      <ResponsiveContainer width="100%" height="100%" onResize={setChartWidth}>
        <ComposedChart
          data={chartData}
          margin={{ top: 12, right: 12, left: -12, bottom: 0 }}
          accessibilityLayer
        >
          <CartesianGrid stroke="hsl(var(--line-1))" strokeDasharray="3 4" vertical={false} />
          <XAxis
            dataKey="x"
            type="number"
            scale="time"
            domain={['dataMin', 'dataMax']}
            ticks={ticks}
            tick={{ fill: 'hsl(var(--text-3))', fontSize: 10 }}
            stroke="transparent"
            tickFormatter={(value: number) => formatAnalyticsBucket(value, granularity, locale)}
            minTickGap={granularity === 'HOUR' || granularity === 'WEEK' ? 70 : 35}
            padding={{ left: xPadding, right: xPadding }}
            axisLine={false}
            tickLine={false}
          />
          <YAxis
            tick={{ fill: 'hsl(var(--text-3))', fontSize: 10 }}
            stroke="transparent"
            width={60}
            tickFormatter={(value: number) => formatNumber(value)}
            axisLine={false}
            tickLine={false}
            allowDecimals={false}
            domain={[0, 'auto']}
          />
          <Tooltip
            trigger={tooltipTrigger}
            content={
              <SingleTooltip color={color} granularity={granularity} valueLabel={valueLabel} />
            }
            filterNull={false}
            cursor={{
              stroke: 'hsl(var(--line-2))',
              strokeDasharray: '3 3',
              fill: 'hsl(var(--bg-2))',
            }}
          />
          {variant === 'bar' ? (
            <Bar
              dataKey="v"
              fill={color}
              maxBarSize={maxBarSize}
              barSize={chartData.length === 1 ? maxBarSize : undefined}
              radius={[4, 4, 0, 0]}
              isAnimationActive={false}
            >
              {chartData.map((point) => (
                <Cell key={point.x} fillOpacity={point.partial ? 0.45 : 0.85} />
              ))}
            </Bar>
          ) : (
            <Line
              type="linear"
              dataKey="v"
              stroke={color}
              strokeWidth={2.5}
              dot={chartData.length <= 45 ? { r: 2, fill: color, strokeWidth: 0 } : false}
              activeDot={{ r: 5 }}
              connectNulls={mode === 'cumulative'}
              isAnimationActive={false}
            />
          )}
        </ComposedChart>
      </ResponsiveContainer>
    </div>
  );
}

function SingleTooltip({
  active,
  payload,
  label,
  color,
  granularity,
  valueLabel,
}: {
  active?: boolean;
  payload?: Array<{ value?: number | null; payload?: AnalyticsChartPoint }>;
  label?: string | number;
  color: string;
  granularity: Granularity;
  valueLabel: string;
}) {
  const { t, locale } = useI18n();
  if (!active || !payload?.length || label === undefined) return null;
  const row = payload[0];
  return (
    <div className="max-w-[230px] rounded-[10px] border border-line-1 bg-bg-1 px-3 py-2 shadow-card">
      <div className="text-[11px] font-medium text-text-3">
        {formatAnalyticsBucket(Number(label), granularity, locale, true)}
      </div>
      <div className="mt-2 flex items-center gap-2">
        <span className="h-2 w-2 shrink-0 rounded-[2px]" style={{ background: color }} />
        <span className="text-[12px] text-text-2">{valueLabel}</span>
        <span className="ml-auto text-[14px] font-semibold tabular-nums text-text-1">
          {row.value === null || row.value === undefined
            ? '—'
            : formatAnalyticsValue(row.value, locale)}
        </span>
      </div>
      {(row.value === null || row.value === undefined) && (
        <p className="mt-1 text-[11px] text-text-3">{t('Donnée manquante', 'Missing data')}</p>
      )}
      {row.payload?.partial && (
        <p className="mt-1 text-[11px] text-amber">
          {t('Période en cours · partielle', 'Current bucket · partial')}
        </p>
      )}
    </div>
  );
}
